/**
 * ### CompositeScenarioMic
 *
 * One microphone of the demo site. `name` is a dictionary key.
 */
export type CompositeScenarioMic = {
  id: string;
  name: string;
};

/**
 * ### CompositeScenarioEvent
 *
 * One detection inside the demo composite event: which of the listed
 * sounds it is, which microphone heard it, how sure the model was, when it
 * started relative to the event, and the clip that plays for it.
 */
export type CompositeScenarioEvent = {
  id: string;
  /** Dictionary key of one of the sounds the page lists */
  label: string;
  micId: string;
  /** Model confidence in `[0, 1]`, as the console shows it */
  confidence: number;
  /** Seconds after the composite event opened */
  startSec: number;
  /** Clip played for this detection, under `/sounds/safety/` */
  audioSrc: string;
};

/**
 * ### COMPOSITE_SCENARIO_CLIP_SEC
 *
 * Every event clip is drawn two seconds long on the timeline, as the
 * console does regardless of the recording's real length.
 */
export const COMPOSITE_SCENARIO_CLIP_SEC = 2;

const MIC_RESTROOM = 'mic-restroom-1f';
const MIC_CORRIDOR = 'mic-corridor-1f';

/**
 * ### SAFETY_COMPOSITE_SCENARIO
 *
 * The demo composite event the Safety page shows: a "위협 상황 (폭행 의심)"
 * rule that fired as a man cornered a woman at the sinks of a first-floor
 * restroom.
 * She shouts him off, screams, and calls for help, then screams again at the
 * door and gets out to the corridor still calling for help. The rule is named
 * for the situation rather than a fight because the recording is one
 * woman's voice throughout: a victim, not two people trading blows. It says
 * the assault is suspected, not established, because sound alone cannot
 * establish it; the pop-up sets the qualifier on its own line.
 *
 * It is one acoustic scene heard by two microphones. The restroom mic hears
 * the shouting, screams, and first call for help up close; the corridor mic
 * hears the same scene faintly through the wall, then clearly once the
 * scream reaches the door and the call for help comes from the corridor
 * itself. Each clip is the two seconds its microphone heard from
 * that detection on, so overlapping fragments share audio the way console
 * clips do.
 *
 * The voices are one recording of a female scream, an aggressive yell, and
 * "살려주세요", supplied for this demo and not taken from a customer site;
 * the room, the distance, and the order are staged. English pages leave
 * the two rescue-call detections off, as the English sound list does. All
 * values are demo data.
 */
export const SAFETY_COMPOSITE_SCENARIO = {
  ruleName: '위협 상황 (폭행 의심)',
  /**
   * Staged photograph of the situation the rule fired on. It stands beside
   * the pop-up, never inside it: the product hears the room, it does not
   * film it, and the caption says the scene is acted.
   */
  scene: {
    src: '/images/apps/solution/safety/composite-scene-assault.webp',
    width: 1448,
    height: 1086,
    alt: '화장실 세면대 앞에서 한 남성이 여성에게 다가가 위협하는 연출 장면',
    caption: '연출 장면',
  },
  ruleDescription: '고함·비명·구조 요청·충격음이 8초 안에 2건 이상',
  location: '1층 화장실',
  durationSec: 8,
  mics: [
    { id: MIC_RESTROOM, name: '1층 화장실' },
    { id: MIC_CORRIDOR, name: '1층 복도' },
  ] as ReadonlyArray<CompositeScenarioMic>,
  events: [
    {
      id: 'shout-1',
      label: '여성 고성',
      micId: MIC_RESTROOM,
      confidence: 0.83,
      startSec: 0.3,
      audioSrc: '/sounds/safety/assault-restroom-shout-1.wav',
    },
    {
      id: 'scream-1',
      label: '여성 비명',
      micId: MIC_RESTROOM,
      confidence: 0.95,
      startSec: 1.2,
      audioSrc: '/sounds/safety/assault-restroom-scream-1.wav',
    },
    {
      id: 'rescue-1',
      label: '살려주세요',
      micId: MIC_RESTROOM,
      confidence: 0.91,
      startSec: 2.3,
      audioSrc: '/sounds/safety/assault-restroom-rescue-1.wav',
    },
    {
      id: 'shout-2',
      label: '여성 고성',
      micId: MIC_RESTROOM,
      confidence: 0.78,
      startSec: 3.9,
      audioSrc: '/sounds/safety/assault-restroom-shout-2.wav',
    },
    {
      id: 'scream-2',
      label: '여성 비명',
      micId: MIC_RESTROOM,
      confidence: 0.89,
      startSec: 5.0,
      audioSrc: '/sounds/safety/assault-restroom-scream-2.wav',
    },
    {
      id: 'scream-3',
      label: '여성 비명',
      micId: MIC_CORRIDOR,
      confidence: 0.81,
      startSec: 5.05,
      audioSrc: '/sounds/safety/assault-corridor-scream-1.wav',
    },
    {
      id: 'rescue-2',
      label: '살려주세요',
      micId: MIC_CORRIDOR,
      confidence: 0.93,
      startSec: 6.0,
      audioSrc: '/sounds/safety/assault-corridor-rescue-1.wav',
    },
  ] as ReadonlyArray<CompositeScenarioEvent>,
};
